// -----------------------------------------
// 2020.7 -  filipp@explainitapp.com
// -----------------------------------------


export default class BarcodeDecoder {

    constructor(imageData) {
        this.imageData = imageData;
        if (this.debugMode) console.log(this.width + "X" + this.height);
    }

    get bitTable() {
        return {
            L: [
                '0001101',
                '0011001',
                '0010011',
                '0111101',
                '0100011',
                '0110001',
                '0101111',
                '0111011',
                '0110111',
                '0001011'
            ],
            R: [
                '1110010',
                '1100110',
                '1101100',
                '1000010',
                '1011100',
                '1001110',
                '1010000',
                '1000100',
                '1001000',
                '1110100'
            ],
            G: [
                '0100111',
                '0110011',
                '0011011',
                '0100001',
                '0011101',
                '0111001',
                '0000101',
                '0010001',
                '0001001',
                '0010111'
            ]
        }
    }

    get lgTable() {
        return [
            'LLLLLL',
            'LLGLGG',
            'LLGGLG',
            'LLGGGL',
            'LGLLGG',
            'LGGLLG',
            'LGGGLL',
            'LGLGLG',
            'LGLGGL',
            'LGGLGL'
        ]
    }

    get width() {
        return this.imageData.width
    }

    get height() {
        return this.imageData.height
    }

    get _rgbLineSize() {
        return this.imageData.width * 4
    }

    imageData;
    bWImageData;
    reducedData;
    bestRecognitionLine = {};
    calibrationData = {};
    decodedBits = [];

    histogram = [];
    histogramMin = 0;
    histogramMax = 255;
    threshold = 150;

    /**
     * DEBUG MODE - for logging set true
     */
    debugMode = false;


    getMonoBase64ImgData() {
        const dataArray = this.reducedData.reduce((acc, v) => {
            const val = (v === 1) ? 0 : (v === 0) ? 255 : 127;
            const aph = v === null ? 0 : (v === 2 ? 200 : 255);
            acc.push(val);
            acc.push(val);
            acc.push(val);
            acc.push(aph);

            return acc;
        }, []);

        const clampedArr = new Uint8ClampedArray(dataArray);
        const imageData = new ImageData(clampedArr, this.width, this.height);


        const canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        const ctx = canvas.getContext("2d");

        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL();
    }


    static imageDataFromBase64(base64String) {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext("2d");

            const image = new Image();
            image.onload = function () {
                canvas.width = image.width;
                canvas.height = image.height;
                ctx.drawImage(image, 0, 0);
                resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
            };
            image.src = base64String.includes(";base64,") ? base64String : 'data:image/png;base64,' + base64String;
        });

    }


    _calcControlNumber(code) {
        if (!code) {
            return null;
        }

        const r = code.split('').map(it => parseInt(it));

        const a = r[1] + r[3] + r[5] + r[7] + r[9] + r[11];
        const b = r[0] + r[2] + r[4] + r[6] + r[8] + r[10];

        const s = (3 * a + b).toString();
        const controlNumber = 10 - parseInt(s[s.length - 1]);

        return controlNumber.toString();
    }


    _isControlSumOk(code) {
        const controlNumber = this._calcControlNumber(code);

        return code[12] === controlNumber;
    }


    _fixCodeWithControlNum(code) {
        const controlNumber = this._calcControlNumber(code);
        code[12] = controlNumber;

        return code;
    }


    _byteTable(byte) {
        return this.bitTable.L.includes(byte) ? 'L' : this.bitTable.G.includes(byte) ? 'G' : this.bitTable.R.includes(byte) ? 'R' : null;
    }


    _digitForByte(byte, side) {
        if (side === "L") return  this.bitTable.L.includes(byte) ? this.bitTable.L.indexOf(byte) : this.bitTable.G.includes(byte) ? this.bitTable.G.indexOf(byte) : null;
        if (side === "R") return  this.bitTable.R.includes(byte) ? this.bitTable.R.indexOf(byte) : null;
        return null;
    }


    _getFixedByte(side /*L or R*/, byte, variantsOfByte = [], parityList = []) {
        let fixedByte = byte;

        let parityOk = this._digitForByte(byte, side) !== null && !!this.lgTable.find(LG => LG.includes(parityList.join('') + this._byteTable(byte)));

        if (!parityOk && variantsOfByte.length > 0) {

            if (fixedByte.match(/00$/g)) {
                // for right part bytes ...
                fixedByte = fixedByte.slice(0, fixedByte.length - 1);
                if (this._digitForByte(fixedByte, side) !== null) return fixedByte;
            }

            if (fixedByte.match(/10001$/g)) {
                // for left part bytes ...
                fixedByte = fixedByte.slice(0, fixedByte.length - 2) + '1';
                let parityOk = this._digitForByte(fixedByte, side) !== null && !!this.lgTable.find(LG => LG.includes(parityList.join('') + this._byteTable(fixedByte)));
                if (this.debugMode) console.log({fixedByte, parityOk});
                if (parityOk) return fixedByte;
            }

            fixedByte = variantsOfByte.find(it => {

                const parityOk = side === "R" ? true : !!this.lgTable.find(LG => LG.includes(parityList.join('') + this._byteTable(it)));
                return parityOk && this._digitForByte(it, side) !== null
            });

            if (!fixedByte && variantsOfByte.length > 0) fixedByte = variantsOfByte[0];
        }

        return fixedByte;
    }


    _pixelBWValue([r, g, b]) {
        return Math.round((r + g + b) / 3)
    }


    _reducedPixelVal(bwValue) {
        // if (bwValue >= 165) return 0; //150
        if (bwValue > this.threshold ) return 0; //150
        return 1;
    }


    _findVerticalLimits() {
        const data = this.bestRecognitionLine;

        // 1. Find top limit
        for (let y = data.y; y > 0; y--) {
            let lineIsBreak = false;

            for (let x = data.x0 + 10; x < data.x1 - 30; x++) {
                const val = this.reducedData[y * this.width + x];
                if (val === 1) {
                    lineIsBreak = true;
                    break;
                }
            }

            if (!lineIsBreak) {
                this.bestRecognitionLine.y0 = y;
                break;
            }
        }

        // 2. Find bottom limits
        for (let y = data.y; y < this.height; y++) {
            let lineBreakTimes = 0;
            let lineIsBreak = false;


            for (let x = data.x0 + 10; x < data.x1 - 10; x++) {
                const val = this.reducedData[y * this.width + x];
                if (val === 1) {
                    lineBreakTimes++;
                    lineIsBreak = true;
                }
            }

            if (lineBreakTimes >= 6 && lineBreakTimes < 15) {
                // if found L, G, R lines bottom
                this.bestRecognitionLine.y1 = y;
            }

            if (!lineIsBreak) {
                // if found barcode white bottom
                this.bestRecognitionLine.y2 = y;
                if (!this.bestRecognitionLine.y1)
                    this.bestRecognitionLine.y1 = Math.round(this.bestRecognitionLine.y0 + (y - this.bestRecognitionLine.y0) * 0.8);
                break;
            }
        }

    }


    _getVariantsForBrokenByte(byte) {
        let variantsOfByte = [];

        // try to remove extra 0
        if (byte.length > 7) byte = byte.replace("00000", "0000");
        if (byte.length > 7) byte = byte.replace("11111", "1111");

        if (byte.length > 7 && byte.match(/00/g)) {

            const splitter = '1';
            const vars = byte.split(splitter);

            variantsOfByte =
                vars.map(
                    (it, i, arr) => {
                        const newArr = [...arr];
                        newArr[i] = it.includes('00') ? it.replace('0', '') : it;
                        return newArr.join(splitter);
                    })
                    .filter(it => it !== byte);
        }


        if (byte.length > 7 && byte.match(/11/g)) {

            const splitter = '0';
            const vars = byte.split(splitter);

            variantsOfByte = [...variantsOfByte ,
                ...vars.map(
                    (it, i, arr) => {
                        const newArr = [...arr];
                        newArr[i] = it.includes('11') ? it.replace('1', '') : it;
                        return newArr.join(splitter);
                    })
                    .filter(it => it !== byte)
                ]
        }

        return variantsOfByte.length === 0 ? [byte] : variantsOfByte;
    }


    tryToGetResult() {
        if (this.decodedBits.length < 95 || this.decodedBits.length > 110) return false;
        const result = [null];
        const parityList = [];

        let lastByteLength = 7;
        let firsByteStartAt = 3;

        let deltaN = 0;

        for (let n = firsByteStartAt; n < this.decodedBits.length - 3; n += lastByteLength) {
            if (n - deltaN === 45) {
                lastByteLength = 5;
            } else {
                lastByteLength = 7;
            }

            let variantsOfByte = [];

            // LG
            if (lastByteLength > 5 && n < 50) {
                // check if begins from 0 and finishing on 1
                if (this.decodedBits[n] === 1) {
                    if (this.debugMode) console.log("LG byte begins from 1", {result}, this.decodedBits.slice(n, n + lastByteLength).join(''));
                    return false;
                }

                if (this.decodedBits[n + lastByteLength - 1] === 0) {
                    if (this.decodedBits[n + lastByteLength] === 1 || this.decodedBits[n + lastByteLength + 1] === 1) {
                        if (this.decodedBits[n + lastByteLength] === 1) {
                            lastByteLength++
                        } else {
                            lastByteLength += 2
                        }
                        if (this.debugMode) console.log("LG increase", this.decodedBits.slice(n, n + lastByteLength).join(''), n);

                    } else {
                        if (this.debugMode) console.log("LG byte ends on 0 and next is 0", result, n);
                        return false;
                    }
                }

                if (lastByteLength === 7 && this.decodedBits[n + lastByteLength] !== 0) {
                    // next byte will be wrong
                    lastByteLength++;
                }

                if (lastByteLength === 8 && this.decodedBits[n + lastByteLength] !== 0) {
                    // next byte still will be wrong
                    lastByteLength++;
                    if (this.decodedBits[n + lastByteLength] !== 0) {
                        if (this.debugMode) console.log("Critical LG length", n);
                        return false;
                    }
                }

            }

            // R
            if (lastByteLength > 5 && n >= 50) {
                // check if begins from 1 and finishing on 0
                if (this.decodedBits[n] === 0) {
                    if (this.debugMode) console.log("R byte begins from 0", {result}, this.decodedBits.slice(n, n + lastByteLength).join(''));
                    return false;
                }

                if (this.decodedBits[n + lastByteLength - 1] === 1) {

                    if (this.decodedBits[n + lastByteLength] === 0 || this.decodedBits[n + lastByteLength + 1] === 0) {
                        if (this.decodedBits[n + lastByteLength] === 0) {
                            lastByteLength++
                        } else {
                            lastByteLength += 2
                        }
                    } else {
                        if (this.debugMode) console.log("R byte ends on 1 and next is 1");
                        return false;
                    }
                }

                if (lastByteLength === 7 && this.decodedBits[n + lastByteLength] !== 1) {
                    // next byte will be wrong
                    lastByteLength++;
                }

                if (lastByteLength === 8 && this.decodedBits[n + lastByteLength] !== 1) {
                    // next byte still will be wrong
                    lastByteLength++;
                    if (this.decodedBits[n + lastByteLength] !== 1) {
                        if (this.debugMode) console.log("Critical R length", n);
                        return false;
                    }
                }
            }

            let byte = this.decodedBits.slice(n, n + lastByteLength).join('');

            if (lastByteLength === 5 && byte !== '01010') {
                if (this.debugMode) console.log("01010", byte, this.decodedBits.slice(n - 1, n + lastByteLength + 1).join(''));
            }

            if (byte.length > 7) {
                if (this.debugMode) console.log(n - deltaN, byte);

                const variants = this._getVariantsForBrokenByte(byte);
                if (variants.length === 1) {
                    byte = variants[0];
                } else {
                    variantsOfByte = variants;
                }

                if (this.debugMode) console.log(n - deltaN, byte, {variantsOfByte});
            }

            if (n - deltaN <= 40) {
                byte = this._getFixedByte("L", byte, variantsOfByte, parityList);
                if (this.debugMode) console.log({byte});
                parityList.push(this._byteTable(byte) || '?');
            } else if (n - deltaN >= 46) {
                byte = this._getFixedByte("R", byte, variantsOfByte, parityList);
                if (this.debugMode) console.log({byte});

            }

            const digit = this._digitForByte(byte, n - deltaN <= 40 ? "L" : "R");

            if (lastByteLength !== 5 && byte.length >= 7) result.push(digit !== null ? digit : '?');
            if (lastByteLength > 7) deltaN += lastByteLength - 7;
        }

        const parity = parityList.join('');
        const parityNumber = this.lgTable.indexOf(parity);
        if (parityNumber < 0) {
            if (this.debugMode) console.log('wrong parity', parity, parityNumber, result);
            return false;
        }

        result[0] = parityNumber;

        return result.join('');
    }


    decodeBitsFromImg() {
        const {y, x0, x1} = this.bestRecognitionLine;
        const {averageBitSize, averageBlackSize, averageWhiteSize} = this.calibrationData;
        // 1.2
        const averageForColors = [(averageWhiteSize + averageBitSize) / 2, (averageBlackSize + averageBitSize) / 2];
        if (this.debugMode) console.log(averageForColors);

        let nowIs = 0;
        let currentLength = 0;
        let seqIsStarted = false;

        let bitSequence = [];

        for (let x = x0 + 1; x <= x1 + 1; x++) {
            const val = this.reducedData[y * this.width + x];

            if (nowIs !== val) {
                if (seqIsStarted) {
                    const bitCount = Math.round(currentLength / averageForColors[nowIs]);
                    if (!bitCount) return false;

                    const filling = new Array(bitCount).fill(nowIs);

                    bitSequence = [...bitSequence, ...filling];
                }

                nowIs = val;
                currentLength = 0;
                if (!seqIsStarted) seqIsStarted = true;
            }

            currentLength++;
        }

        const bitString = bitSequence.join('');
        if (this.debugMode) console.log({bitString});

        this.decodedBits = bitString.match(/1010[0-1]*01010[0-1]*0101/g) ? bitString.match(/1010[0-1]*01010[0-1]*0101/g)[0].split('').map(el => parseInt(el)) : bitSequence;
    }


    calibrateBitSize() {
        const {y, x0, x1} = this.bestRecognitionLine;

        let minBlackSize = 999;
        let maxBlackSize = 0;
        let minWhiteSize = 999;
        let maxWhiteSize = 0;

        let seqStarted = false;

        let firstBitsSize = []; // [black, white, black]

        // Histogram: key number in array = block width in pixels, value is count of so blocks
        let histogramBlackSize = [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        let histogramWhiteSize = [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

        let nowIs = 0;
        let currentLength = 0;

        for (let x = x0; x < x1 ; x++) {
            const val = this.reducedData[y * this.width + x];

            if (nowIs !== val) {
                if (nowIs === 1) {
                    if (currentLength > maxBlackSize) maxBlackSize = currentLength;
                    if (currentLength < minBlackSize) minBlackSize = currentLength;

                    histogramBlackSize[currentLength]++;
                } else {
                    if (currentLength > maxWhiteSize && seqStarted) maxWhiteSize = currentLength;
                    if (currentLength < minWhiteSize) minWhiteSize = currentLength;

                    histogramWhiteSize[currentLength] !== undefined && histogramWhiteSize[currentLength]++;
                }

                if (firstBitsSize.length < 3) {
                    // store first bits size for calibration
                    if (nowIs === 1 && (firstBitsSize.length === 0 || firstBitsSize.length === 2)) firstBitsSize.push(currentLength);
                    if (nowIs === 0 && firstBitsSize.length === 1) firstBitsSize.push(currentLength);
                }

                nowIs = val;

                currentLength = 0;
            }

            if (val && !seqStarted) seqStarted = true;
            currentLength++;
        }

        const averageBitSize = (minWhiteSize + maxWhiteSize / 4 + minBlackSize + maxBlackSize / 4 + firstBitsSize[0] + firstBitsSize[1] + firstBitsSize[2]) / 7;

        const averageWhiteSize = (minWhiteSize + maxWhiteSize / 4 +  firstBitsSize[1]) / 3;
        const averageBlackSize = (minBlackSize + maxBlackSize / 4 + firstBitsSize[0] + firstBitsSize[2]) / 4;

        this.calibrationData = {
            firstBitsSize,
            minBlackSize,
            maxBlackSize,
            minWhiteSize,
            maxWhiteSize,
            histogramBlackSize,
            histogramWhiteSize,
            averageBitSize,
            averageWhiteSize,
            averageBlackSize
        };

    }


    verticalNormalisation() {
        this._findVerticalLimits();
        const {x0, x1, y0, y1} = this.bestRecognitionLine;


        for (let x = x0; x < x1; x++) {
            let blackCount = 0;
            let whiteCount = 0;

            for (let y = y0; y < y1; y++) {
                const val = this.reducedData[y * this.width + x];
                val === 1 ? blackCount++ : whiteCount++;
            }

            const averageValue = blackCount >= whiteCount ? 1 : 0;
            // const averageValue = blackCount / whiteCount >= 1.1 ? 1 : 0;
            for (let y = y0; y < y1; y++) this.reducedData[y * this.width + x] = averageValue;

        }


    }


    isImageDataContainCode() {
        const yStep = Math.round(this.height / 20);
        let maxInSequenceConcurrency = 0;

        const lineSeqList = {}; // {lineN: [lineN, seqLength, x0, x1], ...}


        for (let y = 0; y < this.height; y += yStep) {
            let whiteInSeq = 0;
            let blackInSeq = 0;
            let blackLinesInSeq = 0;
            let lastSegLength = 0;
            let nowIs = null;

            let whiteBeginFound = false;

            let lineMaxSeq = 0;

            let xBegin = 0;


            for (let x = 0; x < this.width; x++) {
                const val = this.reducedData[y * this.width + x];
                // const val_above = this.reducedData[(y - 1) * this.width + x];
                // const val_under = this.reducedData[(y + 1) * this.width + x];

                if (whiteInSeq === 0 && blackInSeq === 0) xBegin = x;

                if (nowIs !== val) {
                    nowIs = val;
                    lastSegLength = 0;
                    if (nowIs === 1) blackLinesInSeq++;
                }

                val === 1 ? blackInSeq++ : whiteInSeq++;
                lastSegLength++;


                if ((nowIs === 1 && lastSegLength > 20) || (whiteBeginFound && lastSegLength > 20) || lastSegLength > 20) {
                    const thisSeqPower = blackLinesInSeq * blackInSeq * whiteInSeq;
                    if (thisSeqPower > lineMaxSeq) {
                        lineMaxSeq = thisSeqPower;

                        lineSeqList[y] = lineSeqList[y] || [];
                        if ((lineSeqList[y][1] || 0 ) < lineMaxSeq) {
                            lineSeqList[y] = [y, lineMaxSeq, xBegin, x];
                        }

                        if (lineMaxSeq > maxInSequenceConcurrency) {
                            // ??? lineSeqList instead of this
                            this.bestRecognitionLine = {y, x0: (xBegin > 12 ? xBegin - 12 : xBegin), x1: x};

                            maxInSequenceConcurrency = lineMaxSeq;
                        }
                    }

                    whiteInSeq = 0;
                    blackInSeq = 0;
                    blackLinesInSeq = 0;
                    lastSegLength = 0;
                    whiteBeginFound = false;
                } else {
                    if (nowIs === 0 && lastSegLength > 12 && !whiteBeginFound) whiteBeginFound = true;
                }

            }

        }

        /**
         * Searching for more optimal line
         */
        for (let it of Object.values(lineSeqList)) {
            const [line, count, xBegin, x] = it;

            const prevIt = lineSeqList[line - 30] || [];
            const nextIt = lineSeqList[line + 30] || [];

            const relToPrev = count / (prevIt[1] || 1);
            const relToNext = count / (nextIt[1] || 1);


            if (count > (maxInSequenceConcurrency / 5) && (relToPrev < 1.05 && relToPrev > 0.95) && (relToNext < 1.05 && relToNext > 0.95)) {
                this.bestRecognitionLine = {y: line, x0: (xBegin > 12 ? xBegin - 12 : xBegin), x1: x};
                if (this.debugMode) console.log("MORE OPTIMAL LINE FOUND!");
                break;
            }
        }

        return maxInSequenceConcurrency >= 50;
    }


    reduceGrayscaleData() {
        this.reducedData = [];

        for (let pix of this.bWImageData) {
            this.reducedData.push(this._reducedPixelVal(pix));
        }
    }


    makeDataGrayscale() {
        this.bWImageData = [];

        for (let y = 0; y < this.height; y++) {
            for (let x = 0; x < this.width * 4; x += 4) {
                const pos = y * this._rgbLineSize + x;
                const rgb = [this.imageData.data[pos], this.imageData.data[pos + 1], this.imageData.data[pos + 2]];
                const bw = this._pixelBWValue(rgb);

                this.bWImageData.push(bw);
            }
        }

    }


    _getBWThreshold() {
        let i;
        let sum = 0;

        const total = this.bWImageData.length;

        for (i = 1; i < this.histogram.length; ++i) {
            sum += i * this.histogram[i];
        }

        let sumB = 0;
        let wB = 0;
        let wF = 0;
        let mB;
        let mF;
        let max = 0.0;
        let between = 0.0;
        let threshold1 = 0.0;
        let threshold2 = 0.0;

        for (i = 0; i < this.histogram.length; ++i) {
            wB += this.histogram[i];
            if (wB === 0)
                continue;
            wF = total - wB;
            if (wF === 0)
                break;
            sumB += i * this.histogram[i];
            mB = sumB / wB;
            mF = (sum - sumB) / wF;
            between = wB * wF * Math.pow(mB - mF, 2);
            if (between >= max) {
                threshold1 = i;
                if (between > max) {
                    threshold2 = i;
                }
                max = between;
            }
        }

        if (this.debugMode) console.log({threshold1, threshold2});
        return (threshold1 + threshold2) / 2.0;
    }


    normalizeHistogram() {

        for (let i in this.bWImageData) {
            const val = this.bWImageData[i];

            const newVal = Math.round((val - this.histogramMin) / (this.histogramMax - this.histogramMin) * 255);

            this.bWImageData[i] = newVal >= 0 ? Math.min(255, newVal) : 0
        }

        this.makeHistogram();
        this.threshold = this._getBWThreshold();
        if (this.debugMode) console.log({threshold: this.threshold, histogramMin: this.histogramMin, histogramMax: this.histogramMax });
    }


    makeHistogram() {
        const histogram  = [];
        const histogramNullValue = this.bWImageData.length / 1000;


        for (let i = 0; i <= 255; i++) {
            histogram[i] = 0;
        }

        for (let pix of this.bWImageData) {
            histogram[pix]++
        }

        this.histogram = histogram;
        this.histogramMin = 0;
        this.histogramMax = 255;

        // Searching of minimum
        for (let lum in this.histogram) {
            if (this.histogram[lum] >= histogramNullValue) {
                this.histogramMin = lum;
                break;
            }
        }

        // Searching of maximum
        for (let lum = 255; lum >= 0; lum--) {
            if (this.histogram[lum] >= histogramNullValue) {
                this.histogramMax = lum;
                break;
            }
        }

    }


    /**
     * Start point
     */
    decode() {
        this.makeDataGrayscale();

        this.makeHistogram();
        this.normalizeHistogram();

        let result = false;
        const threshold = this.threshold;

        for (let attemptCount = 1; attemptCount <= 10; attemptCount++ ) {

            this.reduceGrayscaleData();

            if (!this.isImageDataContainCode()) {
                continue;
                // return null;
            }

            // this.verticalNormalisation();
            this.calibrateBitSize();

            this.decodeBitsFromImg();

            if (this.debugMode) console.log(this.bestRecognitionLine, this.calibrationData, this.decodedBits);

            result = this.tryToGetResult();
            if (this.debugMode) console.log({result, threshold: this.threshold});

            if (result && result.includes('?')) {
                // code found but has unrecognized item(s)
                if (result.indexOf('?') === 12) {
                    result = this._fixCodeWithControlNum(result);
                    break;
                }
            }

            if (result && this._isControlSumOk(result)) {
                // code found and it is right
                break;
            }

            result = false;
            this.threshold = threshold <= 140 ? this.threshold + 5 : this.threshold - 5;
        }

        return result;
    }

}

